import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getForumPosts } from '@/lib/forum';
import type { ForumPost } from '@/lib/types/forum';
import { MessageSquare, Plus, Loader2, ThumbsUp, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Category() {
  const { slug } = useParams();
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const data = await getForumPosts(slug, page);
        if (data.length < 10) {
          setHasMore(false);
        }
        setPosts(prev => page === 1 ? data : [...prev, ...data]);
      } catch (error) {
        console.error('Error loading posts:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les discussions.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [slug, page, toast]);

  const handleNewDiscussion = () => {
    if (!currentUser) {
      toast({
        title: "Connexion requise",
        description: "Vous devez être connecté pour créer une discussion.",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    navigate(`/forum/new?category=${slug}`);
  };

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-[#54075B]" />
                {posts[0]?.category?.name || 'Discussions'}
              </h1>
              <p className="text-gray-600">
                {posts[0]?.category?.description}
              </p>
            </div>
            <Button
              onClick={handleNewDiscussion}
              className="bg-[#54075B] hover:bg-[#54075B]/90 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nouvelle discussion
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/forum/post/${post.id}`}
                  className="block"
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                            {post.content}
                          </p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <ThumbsUp className="w-4 h-4" />
                              {post.likesCount}
                            </span>
                            <span className="flex items-center gap-1">
                              <MessageCircle className="w-4 h-4" />
                              {post.commentsCount}
                            </span>
                            <span>
                              Par {post.user?.displayName}
                            </span>
                            <span>
                              {format(new Date(post.createdAt), 'PPP', { locale: fr })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}

              {loading && (
                <div className="flex justify-center p-4">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                </div>
              )}

              {!loading && hasMore && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setPage(p => p + 1)}
                >
                  Charger plus
                </Button>
              )}

              {!loading && posts.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucune discussion pour le moment.</p>
                  <Button
                    onClick={handleNewDiscussion}
                    className="mt-4"
                  >
                    Créer la première discussion
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}